import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { useDropdowns } from "@/contexts/DropdownContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Search, Edit, Trash2, DollarSign, TrendingUp, Receipt, Wallet, Save, X, Upload, Download, FileText, Image, File } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCurrency = (amount) => {
  if (!amount) return "₹0";
  // Display exact numbers without Lakhs conversion
  return `₹${Number(amount).toLocaleString('en-IN')}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const BACKEND_URL_COMPONENT = process.env.REACT_APP_BACKEND_URL;
const API_COMPONENT = `${BACKEND_URL_COMPONENT}/api`;

// Project Payment Row Component with inline editing
function ProjectPaymentRow({ payment, token, onUpdate, canWrite }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    total_budget_to_client: payment.total_budget_to_client || 0,
    extra_scope_of_work: payment.extra_scope_of_work || 0,
    amount_received_from_client: payment.amount_received_from_client || 0,
    amount_transferred_to_vendor: payment.amount_transferred_to_vendor || 0
  });
  const [saving, setSaving] = useState(false);

  // Calculate auto fields locally for immediate feedback
  const totalBudgetWithGst = editData.total_budget_to_client * 1.18;
  const extraScopeWithGst = editData.extra_scope_of_work * 1.18;
  const finalBudget = totalBudgetWithGst + extraScopeWithGst;
  const projectBalance = finalBudget - editData.amount_received_from_client;

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_COMPONENT}/finance/project-payments/${payment.project_id}`, editData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Payment data updated successfully");
      setIsEditing(false);
      onUpdate(); // Refresh data
    } catch (error) {
      toast.error("Failed to update payment data");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditData({
      total_budget_to_client: payment.total_budget_to_client || 0,
      extra_scope_of_work: payment.extra_scope_of_work || 0,
      amount_received_from_client: payment.amount_received_from_client || 0,
      amount_transferred_to_vendor: payment.amount_transferred_to_vendor || 0
    });
    setIsEditing(false);
  };

  const formatCurrencyLocal = (amount) => {
    if (!amount) return "₹0";
    // Display exact numbers without Lakhs conversion
    return `₹${Number(amount).toLocaleString('en-IN')}`;
  };

  return (
    <TableRow className="hover:bg-slate-50 dark:hover:bg-slate-700/50" data-testid={`project-payment-row-${payment.id}`}>
      {/* Project Name */}
      <TableCell>
        <div>
          <p className="font-medium text-slate-900 dark:text-white">{payment.project_name}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">{payment.client}</p>
          <Badge variant="outline" className="text-xs mt-1">{payment.project_id}</Badge>
        </div>
      </TableCell>

      {/* Column 1: Total Budget (Without GST) - Manual */}
      <TableCell className="text-right">
        {isEditing ? (
          <Input
            type="number"
            value={editData.total_budget_to_client}
            onChange={(e) => setEditData({...editData, total_budget_to_client: parseFloat(e.target.value) || 0})}
            className="w-28 h-8 text-right text-sm"
          />
        ) : (
          <span className="font-mono text-sm text-slate-700 dark:text-slate-300">
            {formatCurrencyLocal(payment.total_budget_to_client)}
          </span>
        )}
      </TableCell>

      {/* Column 2: Total Budget (With GST) - Auto */}
      <TableCell className="text-right font-mono text-sm text-emerald-600 dark:text-emerald-400 bg-emerald-50/30 dark:bg-emerald-900/10">
        {formatCurrencyLocal(isEditing ? totalBudgetWithGst : payment.total_budget_with_gst)}
      </TableCell>

      {/* Column 3: Extra Scope (Without GST) - Manual */}
      <TableCell className="text-right">
        {isEditing ? (
          <Input
            type="number"
            value={editData.extra_scope_of_work}
            onChange={(e) => setEditData({...editData, extra_scope_of_work: parseFloat(e.target.value) || 0})}
            className="w-28 h-8 text-right text-sm"
          />
        ) : (
          <span className="font-mono text-sm text-slate-700 dark:text-slate-300">
            {formatCurrencyLocal(payment.extra_scope_of_work)}
          </span>
        )}
      </TableCell>

      {/* Column 4: Extra Scope (With GST) - Auto */}
      <TableCell className="text-right font-mono text-sm text-emerald-600 dark:text-emerald-400 bg-emerald-50/30 dark:bg-emerald-900/10">
        {formatCurrencyLocal(isEditing ? extraScopeWithGst : payment.extra_scope_with_gst)}
      </TableCell>

      {/* Column 5: Final Budget - Auto */}
      <TableCell className="text-right font-mono text-sm font-semibold text-indigo-600 dark:text-indigo-400 bg-indigo-50/30 dark:bg-indigo-900/10">
        {formatCurrencyLocal(isEditing ? finalBudget : payment.final_budget)}
      </TableCell>

      {/* Column 6: Amount Received - Manual */}
      <TableCell className="text-right">
        {isEditing ? (
          <Input
            type="number"
            value={editData.amount_received_from_client}
            onChange={(e) => setEditData({...editData, amount_received_from_client: parseFloat(e.target.value) || 0})}
            className="w-28 h-8 text-right text-sm"
          />
        ) : (
          <span className="font-mono text-sm font-semibold text-emerald-600 dark:text-emerald-400">
            {formatCurrencyLocal(payment.amount_received_from_client)}
          </span>
        )}
      </TableCell>

      {/* Column 7: Amount to Vendor - Manual */}
      <TableCell className="text-right">
        {isEditing ? (
          <Input
            type="number"
            value={editData.amount_transferred_to_vendor}
            onChange={(e) => setEditData({...editData, amount_transferred_to_vendor: parseFloat(e.target.value) || 0})}
            className="w-28 h-8 text-right text-sm"
          />
        ) : (
          <span className="font-mono text-sm font-semibold text-orange-600 dark:text-orange-400">
            {formatCurrencyLocal(payment.amount_transferred_to_vendor)}
          </span>
        )}
      </TableCell>

      {/* Column 8: Project Balance - Auto */}
      <TableCell className={`text-right font-mono text-sm font-bold bg-slate-100/50 dark:bg-slate-700/30 ${
        (isEditing ? projectBalance : payment.project_balance) > 0 
          ? 'text-amber-600 dark:text-amber-400' 
          : 'text-emerald-600 dark:text-emerald-400'
      }`}>
        {formatCurrencyLocal(isEditing ? projectBalance : payment.project_balance)}
      </TableCell>

      {/* Actions */}
      <TableCell className="text-center">
        {canWrite && (
          isEditing ? (
            <div className="flex items-center justify-center gap-1">
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleSave} disabled={saving}>
                <Save className="h-4 w-4 text-emerald-600" />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleCancel}>
                <X className="h-4 w-4 text-slate-500" />
              </Button>
            </div>
          ) : (
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setIsEditing(true)}>
              <Edit className="h-4 w-4 text-slate-500" />
            </Button>
          )
        )}
      </TableCell>
    </TableRow>
  );
}

export default function FinancePage() {
  const { token, hasPermission } = useAuth();
  const { getOptions, getColor } = useDropdowns();
  const [activeTab, setActiveTab] = useState("expenses");
  const [expenses, setExpenses] = useState([]);
  const [projectPayments, setProjectPayments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  // Invoice state
  const [invoices, setInvoices] = useState([]);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  const [invoiceSearchTerm, setInvoiceSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    user_name: "",
    transaction_date: new Date().toISOString().split("T")[0],
    project_id: "",
    project_name: "",
    description: "",
    payment_mode: "Bank Transfer",
    amount: 0,
    remarks: "",
    status: "Pending"
  });

  const canWrite = hasPermission("finance:write");
  const canDelete = hasPermission("finance:delete");

  // Get dropdown options
  const statusOptions = getOptions("expenses", "status");
  const paymentModeOptions = getOptions("expenses", "payment_mode");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [expensesRes, paymentsRes, summaryRes, projectsRes, invoicesRes] = await Promise.all([
        axios.get(`${API}/finance/expenses`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/finance/project-payments`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/finance/summary`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/finance/invoices`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setExpenses(expensesRes.data);
      setProjectPayments(paymentsRes.data);
      setSummary(summaryRes.data);
      setProjects(projectsRes.data);
      setInvoices(invoicesRes.data || []);
    } catch (error) {
      console.error("Failed to fetch finance data", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingExpense) {
        await axios.put(`${API}/finance/expenses/${editingExpense.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("General expense updated");
      } else {
        await axios.post(`${API}/finance/expenses`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("General expense created");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to save general expense");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this general expense?")) return;
    try {
      await axios.delete(`${API}/finance/expenses/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("General expense deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete general expense");
    }
  };

  const openEditDialog = (expense) => {
    setEditingExpense(expense);
    setFormData({
      user_name: expense.user_name,
      transaction_date: expense.transaction_date,
      project_id: expense.project_id || "",
      project_name: expense.project_name || "",
      description: expense.description,
      payment_mode: expense.payment_mode,
      amount: expense.amount,
      remarks: expense.remarks || "",
      status: expense.status
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingExpense(null);
    setFormData({
      user_name: "",
      transaction_date: new Date().toISOString().split("T")[0],
      project_id: "",
      project_name: "",
      description: "",
      payment_mode: "Bank Transfer",
      amount: 0,
      remarks: "",
      status: "Pending"
    });
  };

  const handleProjectChange = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    setFormData({
      ...formData,
      project_id: projectId,
      project_name: project?.name || ""
    });
  };

  // Filter expenses
  const filteredExpenses = expenses.filter(exp => {
    const matchesSearch = exp.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      exp.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      exp.project_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === "all" || exp.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Filter project payments
  const filteredPayments = projectPayments.filter(pp => 
    pp.project_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pp.client?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const hasActiveFilters = filterStatus !== "all";
  const clearFilters = () => setFilterStatus("all");

  // Filter invoices
  const filteredInvoices = invoices.filter(inv =>
    inv.invoice_name?.toLowerCase().includes(invoiceSearchTerm.toLowerCase()) ||
    inv.original_filename?.toLowerCase().includes(invoiceSearchTerm.toLowerCase())
  );

  // Invoice upload handler
  const handleInvoiceUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingInvoice(true);
    const formData = new FormData();
    
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }

    try {
      await axios.post(`${API}/finance/invoices/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(`Successfully uploaded ${files.length} invoice(s)`);
      fetchData();
      setInvoiceDialogOpen(false);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to upload invoice(s)";
      toast.error(errorMsg);
    } finally {
      setUploadingInvoice(false);
      e.target.value = ''; // Reset file input
    }
  };

  // Invoice download handler
  const handleInvoiceDownload = async (invoice) => {
    try {
      const response = await axios.get(`${API}/finance/invoices/${invoice.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', invoice.original_filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Invoice downloaded");
    } catch (error) {
      toast.error("Failed to download invoice");
    }
  };

  // Invoice delete handler
  const handleInvoiceDelete = async (invoiceId) => {
    if (!window.confirm("Delete this invoice?")) return;
    try {
      await axios.delete(`${API}/finance/invoices/${invoiceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Invoice deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete invoice");
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Get file type icon
  const getFileIcon = (fileType) => {
    switch (fileType?.toUpperCase()) {
      case 'PDF':
        return <FileText className="h-5 w-5 text-red-500" />;
      case 'JPG':
      case 'JPEG':
      case 'PNG':
        return <Image className="h-5 w-5 text-blue-500" />;
      case 'DOCX':
      case 'DOC':
        return <FileText className="h-5 w-5 text-blue-600" />;
      default:
        return <File className="h-5 w-5 text-slate-500" />;
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center bg-slate-50 dark:bg-slate-900 min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brillien-600"></div>
      </div>
    );
  }

  return (
    <div className="p-8 bg-slate-50 dark:bg-slate-900 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
            Finance & Payments
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            Manage general expenses and project payments
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-6 border-slate-200 dark:border-slate-700 dark:bg-slate-800 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
              <DollarSign className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Total Budget</p>
              <p className="font-mono text-xl font-bold text-slate-900 dark:text-white">{formatCurrency(summary.total_budget)}</p>
            </div>
          </div>
        </Card>
        <Card className="p-6 border-slate-200 dark:border-slate-700 dark:bg-slate-800 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Received</p>
              <p className="font-mono text-xl font-bold text-emerald-600 dark:text-emerald-400">{formatCurrency(summary.total_received)}</p>
            </div>
          </div>
        </Card>
        <Card className="p-6 border-slate-200 dark:border-slate-700 dark:bg-slate-800 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/50 flex items-center justify-center">
              <Receipt className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Total Expenses</p>
              <p className="font-mono text-xl font-bold text-orange-600 dark:text-orange-400">{formatCurrency(summary.total_expenses)}</p>
            </div>
          </div>
        </Card>
        <Card className="p-6 border-slate-200 dark:border-slate-700 dark:bg-slate-800 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center">
              <Wallet className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Outstanding</p>
              <p className="font-mono text-xl font-bold text-amber-600 dark:text-amber-400">{formatCurrency(summary.outstanding)}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6 bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
          <TabsTrigger value="expenses" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 rounded-md dark:text-slate-300" data-testid="tab-expenses">
            General Expenses
          </TabsTrigger>
          <TabsTrigger value="project-payments" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 rounded-md dark:text-slate-300" data-testid="tab-project-payments">
            Project Payments
          </TabsTrigger>
          <TabsTrigger value="invoices" className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 rounded-md dark:text-slate-300" data-testid="tab-invoices">
            Invoices
          </TabsTrigger>
        </TabsList>

        {/* General Expenses Tab */}
        <TabsContent value="expenses">
          {/* Search, Filters, and Add Button */}
          <Card className="p-4 border-slate-200 dark:border-slate-700 dark:bg-slate-800 rounded-lg mb-6">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search by user, description, or project..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    data-testid="expenses-search-input"
                    className="pl-10 h-10 rounded-lg border-slate-300 dark:border-slate-400 dark:bg-slate-700 dark:text-white"
                  />
                </div>
                {canWrite && (
                  <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
                    <DialogTrigger asChild>
                      <Button className="bg-brillien-600 hover:bg-brillien-700 text-white rounded-lg h-10 px-4" data-testid="add-expense-button">
                        <Plus className="h-4 w-4 mr-2" />Add General Expense
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl dark:bg-slate-800 dark:border-slate-700">
                      <DialogHeader>
                        <DialogTitle>{editingExpense ? "Edit General Expense" : "Add New General Expense"}</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>User Name *</Label>
                            <Input
                              value={formData.user_name}
                              onChange={(e) => setFormData({...formData, user_name: e.target.value})}
                              required
                            />
                          </div>
                          <div>
                            <Label>Transaction Date *</Label>
                            <Input
                              type="date"
                              value={formData.transaction_date}
                              onChange={(e) => setFormData({...formData, transaction_date: e.target.value})}
                              required
                            />
                          </div>
                        </div>
                        <div>
                          <Label>Project (Optional)</Label>
                          <Select value={formData.project_id || "none"} onValueChange={(v) => handleProjectChange(v === "none" ? "" : v)}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a project" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No Project</SelectItem>
                              {projects.map(p => (
                                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Description *</Label>
                          <Input
                            value={formData.description}
                            onChange={(e) => setFormData({...formData, description: e.target.value})}
                            required
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Payment Mode *</Label>
                            <Select value={formData.payment_mode} onValueChange={(v) => setFormData({...formData, payment_mode: v})}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {paymentModeOptions.map(opt => (
                                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Amount (₹) *</Label>
                            <Input
                              type="number"
                              value={formData.amount}
                              onChange={(e) => setFormData({...formData, amount: parseFloat(e.target.value) || 0})}
                              required
                            />
                          </div>
                        </div>
                        <div>
                          <Label>Remarks</Label>
                          <Textarea
                            value={formData.remarks}
                            onChange={(e) => setFormData({...formData, remarks: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>Status</Label>
                          <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {statusOptions.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex justify-end gap-2 pt-4">
                          <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                          <Button type="submit" className="bg-brillien-600 hover:bg-brillien-700">
                            {editingExpense ? "Update" : "Create"} Expense
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
              <div className="flex flex-wrap gap-3 items-center">
                <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Filters:</span>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-36 h-9 rounded-lg dark:border-slate-400 dark:bg-slate-700" data-testid="filter-status">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                    <SelectItem value="all">All Status</SelectItem>
                    {statusOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500 dark:text-slate-400 h-9">
                    Clear All
                  </Button>
                )}
                <span className="text-sm text-slate-500 dark:text-slate-400 ml-auto">
                  {filteredExpenses.length} of {expenses.length} expenses
                </span>
              </div>
            </div>
          </Card>

          {/* Expenses Table */}
          <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800 rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 dark:bg-slate-700/50">
                  <TableHead className="font-semibold dark:text-slate-300 w-16">Sl. No</TableHead>
                  <TableHead className="font-semibold dark:text-slate-300">User Name</TableHead>
                  <TableHead className="font-semibold dark:text-slate-300">Transaction Date</TableHead>
                  <TableHead className="font-semibold dark:text-slate-300">Project Name</TableHead>
                  <TableHead className="font-semibold dark:text-slate-300">Description</TableHead>
                  <TableHead className="font-semibold dark:text-slate-300">Payment Mode</TableHead>
                  <TableHead className="font-semibold dark:text-slate-300 text-right">Amount</TableHead>
                  <TableHead className="font-semibold dark:text-slate-300">Status</TableHead>
                  <TableHead className="font-semibold dark:text-slate-300">Created</TableHead>
                  <TableHead className="font-semibold dark:text-slate-300">Updated</TableHead>
                  <TableHead className="font-semibold dark:text-slate-300 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredExpenses.map((expense, index) => (
                  <TableRow key={expense.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50" data-testid={`expense-row-${expense.id}`}>
                    <TableCell className="font-mono text-sm text-slate-600 dark:text-slate-400">{index + 1}</TableCell>
                    <TableCell className="font-medium text-slate-900 dark:text-white">{expense.user_name}</TableCell>
                    <TableCell className="text-sm text-slate-700 dark:text-slate-300">{formatDate(expense.transaction_date)}</TableCell>
                    <TableCell className="text-sm text-slate-700 dark:text-slate-300">{expense.project_name || "-"}</TableCell>
                    <TableCell className="text-sm text-slate-700 dark:text-slate-300 max-w-xs truncate" title={expense.description}>{expense.description}</TableCell>
                    <TableCell className="text-sm text-slate-700 dark:text-slate-300">{expense.payment_mode}</TableCell>
                    <TableCell className="text-right font-mono font-semibold text-slate-900 dark:text-white">₹{expense.amount?.toLocaleString()}</TableCell>
                    <TableCell>
                      <Badge className={`${getColor("expenses", "status", expense.status)} border text-xs`}>{expense.status}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600 dark:text-slate-400">{formatDate(expense.created_at)}</TableCell>
                    <TableCell className="text-sm text-slate-600 dark:text-slate-400">{formatDate(expense.updated_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {canWrite && (
                          <Button variant="ghost" size="sm" onClick={() => openEditDialog(expense)} className="h-8 w-8 p-0">
                            <Edit className="h-4 w-4 text-slate-600" />
                          </Button>
                        )}
                        {canDelete && (
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(expense.id)} className="h-8 w-8 p-0">
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {filteredExpenses.length === 0 && (
              <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                <Receipt className="h-12 w-12 mx-auto mb-3 text-slate-300 dark:text-slate-600" />
                <p>No general expenses found</p>
                {canWrite && <p className="text-sm mt-1">Click "Add General Expense" to create one</p>}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Project Payments Tab */}
        <TabsContent value="project-payments">
          {/* Search */}
          <Card className="p-4 border-slate-200 dark:border-slate-700 dark:bg-slate-800 rounded-lg mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by project name or client..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="project-payments-search-input"
                className="pl-10 h-10 rounded-lg border-slate-300 dark:border-slate-400 dark:bg-slate-700 dark:text-white"
              />
            </div>
          </Card>

          {/* Project Payments Table */}
          <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-700/50">
                    <TableHead className="font-semibold dark:text-slate-300 min-w-[180px]">Project Name</TableHead>
                    <TableHead className="font-semibold dark:text-slate-300 text-right min-w-[140px]">
                      <div>Total Budget</div>
                      <div className="text-xs font-normal text-slate-400">(Without GST)</div>
                      <div className="text-xs font-normal text-blue-500">Manual</div>
                    </TableHead>
                    <TableHead className="font-semibold dark:text-slate-300 text-right min-w-[130px]">
                      <div>Total Budget</div>
                      <div className="text-xs font-normal text-slate-400">(With GST)</div>
                      <div className="text-xs font-normal text-green-500">Auto</div>
                    </TableHead>
                    <TableHead className="font-semibold dark:text-slate-300 text-right min-w-[140px]">
                      <div>Extra Scope</div>
                      <div className="text-xs font-normal text-slate-400">(Without GST)</div>
                      <div className="text-xs font-normal text-blue-500">Manual</div>
                    </TableHead>
                    <TableHead className="font-semibold dark:text-slate-300 text-right min-w-[130px]">
                      <div>Extra Scope</div>
                      <div className="text-xs font-normal text-slate-400">(With GST)</div>
                      <div className="text-xs font-normal text-green-500">Auto</div>
                    </TableHead>
                    <TableHead className="font-semibold dark:text-slate-300 text-right min-w-[130px]">
                      <div>Final Budget</div>
                      <div className="text-xs font-normal text-green-500">Auto (Col2 + Col4)</div>
                    </TableHead>
                    <TableHead className="font-semibold dark:text-slate-300 text-right min-w-[140px]">
                      <div>Amount Received</div>
                      <div className="text-xs font-normal text-blue-500">Manual</div>
                    </TableHead>
                    <TableHead className="font-semibold dark:text-slate-300 text-right min-w-[140px]">
                      <div>Transferred to Vendor</div>
                      <div className="text-xs font-normal text-blue-500">Manual</div>
                    </TableHead>
                    <TableHead className="font-semibold dark:text-slate-300 text-right min-w-[130px]">
                      <div>Project Balance</div>
                      <div className="text-xs font-normal text-green-500">Auto (Col5 - Col6)</div>
                    </TableHead>
                    <TableHead className="font-semibold dark:text-slate-300 text-center min-w-[80px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPayments.map((pp) => (
                    <ProjectPaymentRow 
                      key={pp.id} 
                      payment={pp} 
                      token={token}
                      onUpdate={fetchData}
                      canWrite={canWrite}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
            {filteredPayments.length === 0 && (
              <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                <DollarSign className="h-12 w-12 mx-auto mb-3 text-slate-300 dark:text-slate-600" />
                <p>No project payment data found</p>
              </div>
            )}
          </Card>

          {/* Totals Summary */}
          {filteredPayments.length > 0 && (
            <Card className="p-4 border-slate-200 dark:border-slate-700 dark:bg-slate-800 rounded-lg mt-4 bg-slate-50">
              <div className="grid grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Total Final Budget</p>
                  <p className="text-xl font-bold text-slate-900 dark:text-white">
                    {formatCurrency(filteredPayments.reduce((sum, p) => sum + (p.final_budget || 0), 0))}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Total Received</p>
                  <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                    {formatCurrency(filteredPayments.reduce((sum, p) => sum + (p.amount_received_from_client || 0), 0))}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Total to Vendors</p>
                  <p className="text-xl font-bold text-orange-600 dark:text-orange-400">
                    {formatCurrency(filteredPayments.reduce((sum, p) => sum + (p.amount_transferred_to_vendor || 0), 0))}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Total Balance</p>
                  <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                    {formatCurrency(filteredPayments.reduce((sum, p) => sum + (p.project_balance || 0), 0))}
                  </p>
                </div>
              </div>
            </Card>
          )}
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices">
          <Card className="p-4 border-slate-200 dark:border-slate-700 dark:bg-slate-800 rounded-lg mb-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4 flex-1">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search invoices..."
                    className="pl-10 border-slate-300 dark:border-slate-400 dark:bg-slate-700"
                    value={invoiceSearchTerm}
                    onChange={(e) => setInvoiceSearchTerm(e.target.value)}
                    data-testid="invoice-search"
                  />
                </div>
                <Badge variant="outline" className="text-slate-600 dark:text-slate-400">
                  {filteredInvoices.length} invoice{filteredInvoices.length !== 1 ? 's' : ''}
                </Badge>
              </div>
              
              {canWrite && (
                <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-brillien-600 hover:bg-brillien-700 text-white" data-testid="upload-invoice-btn">
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Invoice
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>Upload Invoice(s)</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div className="border-2 border-dashed border-slate-300 dark:border-slate-400 rounded-lg p-8 text-center hover:border-brillien-500 transition-colors">
                        <Upload className="h-10 w-10 mx-auto text-slate-400 mb-3" />
                        <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                          Drag & drop files here or click to browse
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-500 mb-4">
                          Supported: PDF, DOCX, JPG, PNG
                        </p>
                        <input
                          type="file"
                          multiple
                          accept=".pdf,.docx,.doc,.jpg,.jpeg,.png"
                          onChange={handleInvoiceUpload}
                          className="absolute inset-0 opacity-0 cursor-pointer"
                          disabled={uploadingInvoice}
                          data-testid="invoice-file-input"
                        />
                        <Button 
                          variant="outline" 
                          className="relative"
                          disabled={uploadingInvoice}
                        >
                          {uploadingInvoice ? (
                            <>
                              <span className="h-4 w-4 mr-2 border-2 border-brillien-600 border-t-transparent rounded-full animate-spin"></span>
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="h-4 w-4 mr-2" />
                              Select Files
                            </>
                          )}
                          <input
                            type="file"
                            multiple
                            accept=".pdf,.docx,.doc,.jpg,.jpeg,.png"
                            onChange={handleInvoiceUpload}
                            className="absolute inset-0 opacity-0 cursor-pointer"
                            disabled={uploadingInvoice}
                          />
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500 text-center">
                        You can upload multiple files at once (bulk upload)
                      </p>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </Card>

          {/* Invoices Grid */}
          <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800 rounded-lg overflow-hidden">
            {filteredInvoices.length === 0 ? (
              <div className="p-12 text-center">
                <FileText className="h-12 w-12 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
                <p className="text-slate-500 dark:text-slate-400 font-medium">No invoices uploaded yet</p>
                <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
                  Upload your first invoice to get started
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-900">
                    <TableHead className="text-slate-700 dark:text-slate-300 font-semibold">Type</TableHead>
                    <TableHead className="text-slate-700 dark:text-slate-300 font-semibold">Invoice Name</TableHead>
                    <TableHead className="text-slate-700 dark:text-slate-300 font-semibold">File Name</TableHead>
                    <TableHead className="text-slate-700 dark:text-slate-300 font-semibold">Size</TableHead>
                    <TableHead className="text-slate-700 dark:text-slate-300 font-semibold">Uploaded</TableHead>
                    <TableHead className="text-slate-700 dark:text-slate-300 font-semibold">Uploaded By</TableHead>
                    <TableHead className="text-slate-700 dark:text-slate-300 font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInvoices.map((invoice) => (
                    <TableRow key={invoice.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getFileIcon(invoice.file_type)}
                          <span className="text-xs font-medium text-slate-500 uppercase">{invoice.file_type}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium text-slate-900 dark:text-white">
                        {invoice.invoice_name}
                      </TableCell>
                      <TableCell className="text-slate-600 dark:text-slate-400 text-sm">
                        {invoice.original_filename}
                      </TableCell>
                      <TableCell className="text-slate-600 dark:text-slate-400 text-sm">
                        {formatFileSize(invoice.file_size)}
                      </TableCell>
                      <TableCell className="text-slate-600 dark:text-slate-400 text-sm">
                        {formatDate(invoice.uploaded_at)}
                      </TableCell>
                      <TableCell className="text-slate-600 dark:text-slate-400 text-sm">
                        {invoice.uploaded_by}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleInvoiceDownload(invoice)}
                            className="h-8 w-8 p-0 text-brillien-600 hover:text-brillien-700"
                            title="Download"
                            data-testid={`download-invoice-${invoice.id}`}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          {canDelete && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleInvoiceDelete(invoice.id)}
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-600"
                              title="Delete"
                              data-testid={`delete-invoice-${invoice.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
